import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { deliveriesAPI } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { formatDate, formatDateTime } from "../lib/dateUtils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  User,
  Truck,
  LogIn,
  LogOut,
  Camera,
  CheckCircle,
  X,
  Plus,
  AlertTriangle,
  ExternalLink,
  Download,
  ZoomIn,
  Trash2,
  Phone,
} from "lucide-react";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

// Photo categories for delivery checkout
const PHOTO_CATEGORIES = [
  { key: "left_track", label: "Left Side Track", field: "left_track_photos" },
  { key: "right_track", label: "Right Side Track", field: "right_track_photos" },
  { key: "cabin_glass", label: "Cabin Glass", field: "cabin_glass_photos" },
];

const DeliveryDetail = () => {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef(null);

  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureWarningOpen, setSignatureWarningOpen] = useState(false);
  const [photoWarningOpen, setPhotoWarningOpen] = useState(false);
  const [missingCategories, setMissingCategories] = useState([]);

  const [gettingLocation, setGettingLocation] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutSerialPhoto, setCheckoutSerialPhoto] = useState(null);
  const [deliveryOrderPhoto, setDeliveryOrderPhoto] = useState(null);
  const [checkoutPhotos, setCheckoutPhotos] = useState({
    left_track_photos: [],
    right_track_photos: [],
    cabin_glass_photos: [],
  });
  const [customerSignature, setCustomerSignature] = useState(null);
  const [deliveryOrderNumber, setDeliveryOrderNumber] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  const isDriver = user?.role === "driver";
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  useEffect(() => {
    fetchDelivery();
  }, [deliveryId]);

  // Initialize canvas when signature dialog opens
  useEffect(() => {
    if (signatureDialogOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [signatureDialogOpen]);

  const fetchDelivery = async () => {
    try {
      const response = await deliveriesAPI.getOne(deliveryId);
      setDelivery(response.data);
    } catch (error) {
      toast.error("Failed to load delivery");
      navigate("/deliveries");
    } finally {
      setLoading(false);
    }
  };

  const getGPSLocation = () => {
    return new Promise((resolve, reject) => {
      setGettingLocation(true);
      if (!navigator.geolocation) {
        setGettingLocation(false);
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGettingLocation(false);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          setGettingLocation(false);
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleCheckIn = async () => {
    if (checkingIn) return;
    setCheckingIn(true);
    try {
      const location = await getGPSLocation();
      await deliveriesAPI.checkIn(deliveryId, {
        latitude: location.latitude,
        longitude: location.longitude,
      });
      toast.success("Checked in successfully");
      setCheckInDialogOpen(false);
      fetchDelivery();
    } catch (error) {
      if (error.message?.includes("Geolocation")) {
        toast.error("GPS is required for check-in. Please enable location services.");
      } else {
        toast.error(error.response?.data?.detail || "Check-in failed");
      }
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    // Validate all photo categories have at least 1 photo
    const missing = PHOTO_CATEGORIES.filter(
      (cat) => (checkoutPhotos[cat.field] || []).length < 1
    ).map((cat) => cat.label);

    if (missing.length > 0) {
      setMissingCategories(missing);
      setPhotoWarningOpen(true);
      return;
    }

    // Validate signature
    if (!customerSignature) {
      setSignatureWarningOpen(true);
      return;
    }

    // Validate serial number photo
    if (!checkoutSerialPhoto) {
      toast.error("Please upload a photo of the serial number plate");
      return;
    }

    // Validate delivery order photo
    if (!deliveryOrderPhoto) {
      toast.error("Please upload a photo of the delivery order");
      return;
    }

    // Validate delivery order number
    if (!deliveryOrderNumber || !deliveryOrderNumber.trim()) {
      toast.error("Please enter the delivery order number");
      return;
    }

    if (checkingOut) return;
    setCheckingOut(true);
    try {
      const location = await getGPSLocation();
      await deliveriesAPI.checkOut(deliveryId, {
        latitude: location.latitude,
        longitude: location.longitude,
        left_track_photos: checkoutPhotos.left_track_photos,
        right_track_photos: checkoutPhotos.right_track_photos,
        cabin_glass_photos: checkoutPhotos.cabin_glass_photos,
        serial_number_photo: checkoutSerialPhoto,
        delivery_order_photo: deliveryOrderPhoto,
        delivery_order_number: deliveryOrderNumber.trim(),
        customer_signature: customerSignature,
      });
      toast.success("Delivery completed successfully");
      setCheckOutDialogOpen(false);
      setCheckoutSerialPhoto(null);
      setDeliveryOrderPhoto(null);
      setDeliveryOrderNumber("");
      fetchDelivery();
    } catch (error) {
      if (error.message?.includes("Geolocation")) {
        toast.error("GPS is required for check-out. Please enable location services.");
      } else {
        toast.error(error.response?.data?.detail || "Check-out failed");
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCheckoutSerialPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const compressed = await compressImage(file);
      setCheckoutSerialPhoto(compressed);
    } catch (error) {
      console.error("Failed to compress image:", error);
      toast.error("Failed to process image");
    }
  };

  const handleDeliveryOrderPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const compressed = await compressImage(file);
      setDeliveryOrderPhoto(compressed);
    } catch (error) {
      console.error("Failed to compress image:", error);
      toast.error("Failed to process image");
    }
  };

  const handlePhotoCapture = async (e, categoryField) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentPhotos = checkoutPhotos[categoryField] || [];
    if (currentPhotos.length + files.length > 5) {
      toast.error(`Maximum 5 photos per category. You can add ${5 - currentPhotos.length} more.`);
      return;
    }

    for (const file of files) {
      try {
        const compressed = await compressImage(file, 200);
        setCheckoutPhotos((prev) => ({
          ...prev,
          [categoryField]: [...(prev[categoryField] || []), compressed],
        }));
      } catch (error) {
        toast.error("Failed to process image");
      }
    }
  };

  const handleRemovePhoto = (categoryField, index) => {
    setCheckoutPhotos((prev) => ({
      ...prev,
      [categoryField]: prev[categoryField].filter((_, i) => i !== index),
    }));
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    setCustomerSignature(dataUrl);
    setSignatureDialogOpen(false);
    toast.success("Signature captured");
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      in_progress: "bg-blue-100 text-blue-800 border-blue-300",
      completed: "bg-green-100 text-green-800 border-green-300",
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!delivery) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/deliveries")}
            className="rounded-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold uppercase tracking-tight">
              Delivery Details
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`${getStatusBadge(delivery.status)} rounded-none text-xs`}>
                {delivery.status?.replace("_", " ").toUpperCase()}
              </Badge>
            </div>
          </div>
        </div>

        {/* Check-in/Check-out Buttons */}
        {isDriver && (
          <div className="flex gap-2">
            {!delivery.check_in && (
              <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white rounded-sm"
                    data-testid="check-in-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Check In
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading uppercase">
                      Check In to Delivery Site
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">
                      Your GPS location will be recorded when you check in.
                    </p>
                    <div className="p-4 bg-slate-50 rounded-sm space-y-2">
                      {delivery.pickup_location && (
                        <div className="flex items-center gap-2">
                          <p className="text-sm">
                            <strong className="text-green-700">Pickup From:</strong> {delivery.pickup_location}
                          </p>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.pickup_location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-500 text-white rounded-sm hover:bg-green-600"
                          >
                            <MapPin className="w-3 h-3" />
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      {delivery.delivery_location && (
                        <div className="flex items-center gap-2">
                          <p className="text-sm">
                            <strong className="text-red-700">Deliver To:</strong> {delivery.delivery_location}
                          </p>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.delivery_location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500 text-white rounded-sm hover:bg-red-600"
                          >
                            <MapPin className="w-3 h-3" />
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      <p className="text-sm">
                        <strong>Customer:</strong> {delivery.customer_name}
                      </p>
                    </div>
                    
                    <Button
                      onClick={handleCheckIn}
                      disabled={gettingLocation}
                      className="w-full bg-green-500 hover:bg-green-600 text-white rounded-sm disabled:opacity-50"
                    >
                      {gettingLocation ? "Getting location..." : "Confirm Check In"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {delivery.check_in && !delivery.check_out && (
              <Dialog open={checkOutDialogOpen} onOpenChange={setCheckOutDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                    data-testid="check-out-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Check Out
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading uppercase">
                      Complete Delivery
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6">
                    <p className="text-sm text-slate-500">
                      Upload photos for each part and get customer signature to complete delivery.
                    </p>

                    {/* Photo Categories */}
                    {PHOTO_CATEGORIES.map((category) => {
                      const photos = checkoutPhotos[category.field] || [];
                      const hasMinPhoto = photos.length >= 1;
                      return (
                        <div
                          key={category.key}
                          className={`p-4 rounded-sm border-2 ${
                            hasMinPhoto
                              ? "bg-green-50 border-green-300"
                              : "bg-amber-50 border-amber-300"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`text-sm font-semibold ${
                                hasMinPhoto ? "text-green-700" : "text-amber-700"
                              }`}
                            >
                              {hasMinPhoto ? "✓ " : ""}
                              {category.label} *
                            </span>
                            <span
                              className={`text-xs ${
                                hasMinPhoto ? "text-green-600" : "text-amber-600"
                              }`}
                            >
                              {photos.length}/5 photos
                            </span>
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            {photos.map((photo, index) => (
                              <div key={index} className="relative w-16 h-16">
                                <img
                                  src={photo}
                                  alt={`${category.label} ${index + 1}`}
                                  className="w-full h-full object-cover rounded-sm border border-slate-200 cursor-pointer"
                                  onClick={() => setEnlargedPhoto(photo)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhoto(category.field, index)}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            {photos.length < 5 && (
                              <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center cursor-pointer hover:border-orange-400 bg-white">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  multiple
                                  onChange={(e) => handlePhotoCapture(e, category.field)}
                                  className="hidden"
                                />
                                <Plus className="w-5 h-5 text-slate-400" />
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Serial Number Plate Photo */}
                    <div
                      className={`p-4 rounded-sm border-2 ${
                        checkoutSerialPhoto
                          ? "bg-green-50 border-green-300"
                          : "bg-amber-50 border-amber-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-sm font-semibold ${
                            checkoutSerialPhoto ? "text-green-700" : "text-amber-700"
                          }`}
                        >
                          {checkoutSerialPhoto ? "✓ " : ""}Serial Number Plate Photo *
                        </span>
                      </div>
                      {checkoutSerialPhoto ? (
                        <div className="flex items-center gap-4">
                          <img
                            src={checkoutSerialPhoto}
                            alt="Serial number plate"
                            className="w-32 h-20 object-cover border border-slate-200 rounded-sm cursor-pointer"
                            onClick={() => setEnlargedPhoto(checkoutSerialPhoto)}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCheckoutSerialPhoto(null)}
                            className="rounded-sm"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer p-3 border-2 border-dashed border-amber-300 rounded-sm hover:border-amber-400 bg-white">
                          <Camera className="w-5 h-5 text-amber-500" />
                          <span className="text-sm text-amber-700 font-medium">
                            Take or Upload Serial Number Plate Photo
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={handleCheckoutSerialPhotoUpload}
                            data-testid="checkout-serial-photo-input"
                          />
                        </label>
                      )}
                    </div>

                    {/* Delivery Order Photo */}
                    <div
                      className={`p-4 rounded-sm border-2 ${
                        deliveryOrderPhoto
                          ? "bg-green-50 border-green-300"
                          : "bg-amber-50 border-amber-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-sm font-semibold ${
                            deliveryOrderPhoto ? "text-green-700" : "text-amber-700"
                          }`}
                        >
                          {deliveryOrderPhoto ? "✓ " : ""}Delivery Order Photo *
                        </span>
                      </div>
                      {deliveryOrderPhoto ? (
                        <div className="flex items-center gap-4">
                          <img
                            src={deliveryOrderPhoto}
                            alt="Delivery order"
                            className="w-32 h-20 object-cover border border-slate-200 rounded-sm cursor-pointer"
                            onClick={() => setEnlargedPhoto(deliveryOrderPhoto)}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeliveryOrderPhoto(null)}
                            className="rounded-sm"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer p-3 border-2 border-dashed border-amber-300 rounded-sm hover:border-amber-400 bg-white">
                          <Camera className="w-5 h-5 text-amber-500" />
                          <span className="text-sm text-amber-700 font-medium">
                            Take or Upload Delivery Order Photo
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={handleDeliveryOrderPhotoUpload}
                            data-testid="delivery-order-photo-input"
                          />
                        </label>
                      )}
                    </div>

                    {/* Delivery Order Number Input */}
                    <div
                      className={`p-4 rounded-sm border-2 ${
                        deliveryOrderNumber.trim()
                          ? "bg-green-50 border-green-300"
                          : "bg-amber-50 border-amber-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Label
                          className={`text-sm font-semibold ${
                            deliveryOrderNumber.trim() ? "text-green-700" : "text-amber-700"
                          }`}
                        >
                          {deliveryOrderNumber.trim() ? "✓ " : ""}Delivery Order Number *
                        </Label>
                      </div>
                      <Input
                        value={deliveryOrderNumber}
                        onChange={(e) => setDeliveryOrderNumber(e.target.value)}
                        placeholder="Enter delivery order number"
                        className="rounded-sm"
                        data-testid="delivery-order-number-input"
                      />
                    </div>

                    {/* Customer Signature Section */}
                    <div
                      className={`p-4 rounded-sm border-2 ${
                        customerSignature
                          ? "bg-green-50 border-green-300"
                          : "bg-amber-50 border-amber-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-sm font-semibold ${
                            customerSignature ? "text-green-700" : "text-amber-700"
                          }`}
                        >
                          {customerSignature ? "✓ " : ""}Customer Signature *
                        </span>
                      </div>
                      {customerSignature ? (
                        <div className="flex items-center gap-4">
                          <img
                            src={customerSignature}
                            alt="Customer signature"
                            className="w-32 h-20 border border-slate-200 rounded-sm bg-white"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCustomerSignature(null);
                              setSignatureDialogOpen(true);
                            }}
                            className="rounded-sm"
                          >
                            Re-capture
                          </Button>
                        </div>
                      ) : (
                        <Dialog
                          open={signatureDialogOpen}
                          onOpenChange={setSignatureDialogOpen}
                        >
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="rounded-sm border-amber-300 text-amber-700"
                            >
                              <Camera className="w-4 h-4 mr-2" />
                              Capture Signature
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="font-heading uppercase">
                                Customer Signature
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <p className="text-sm text-slate-500">
                                Please ask the customer to sign below
                              </p>
                              <canvas
                                ref={canvasRef}
                                width={400}
                                height={200}
                                className="border border-slate-300 rounded-sm bg-white w-full touch-none"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                              />
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  onClick={clearSignature}
                                  className="rounded-sm"
                                >
                                  Clear
                                </Button>
                                <Button
                                  onClick={saveSignature}
                                  className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-sm"
                                >
                                  Save Signature
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>

                    <Button
                      onClick={handleCheckOut}
                      disabled={gettingLocation}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                    >
                      {gettingLocation ? "Getting location..." : "Complete Delivery"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>

      {/* Warning Dialogs */}
      <AlertDialog open={photoWarningOpen} onOpenChange={setPhotoWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Photos Required
            </AlertDialogTitle>
            <AlertDialogDescription>
              Please upload at least 1 photo for each category:
              <ul className="list-disc ml-5 mt-2">
                {missingCategories.map((cat) => (
                  <li key={cat}>{cat}</li>
                ))}
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setPhotoWarningOpen(false)}>
              OK, I'll add photos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={signatureWarningOpen} onOpenChange={setSignatureWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Customer Signature Required
            </AlertDialogTitle>
            <AlertDialogDescription>
              Please capture the customer's signature before completing the delivery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSignatureWarningOpen(false)}>
              OK, I'll get the signature
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo Lightbox */}
      <Dialog open={!!enlargedPhoto} onOpenChange={() => setEnlargedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ZoomIn className="w-4 h-4" />
                Photo Preview
              </span>
              <div className="flex gap-2">
                <a
                  href={enlargedPhoto}
                  download="photo.jpg"
                  className="p-2 bg-blue-500 text-white rounded-sm hover:bg-blue-600"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setEnlargedPhoto(null)}
                  className="p-2 bg-slate-500 text-white rounded-sm hover:bg-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {enlargedPhoto && (
            <img
              src={enlargedPhoto}
              alt="Enlarged"
              className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Check-in/out Status */}
      {(delivery.check_in || delivery.check_out) && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-6">
              {delivery.check_in && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
                    Checked In
                  </p>
                  <p className="text-sm text-slate-700">
                    {formatDateTime(delivery.check_in.timestamp)}
                  </p>
                </div>
              )}
              {delivery.check_out && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
                    Checked Out
                  </p>
                  <p className="text-sm text-slate-700">
                    {formatDateTime(delivery.check_out.timestamp)}
                  </p>
                </div>
              )}
              {delivery.check_out?.delivery_order_number && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-green-600 font-semibold">
                    Delivery Order #
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    {delivery.check_out.delivery_order_number}
                  </p>
                </div>
              )}
              {!delivery.check_out?.delivery_order_number && delivery.delivery_order_number && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-green-600 font-semibold">
                    Delivery Order #
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    {delivery.delivery_order_number}
                  </p>
                </div>
              )}
              {delivery.distance_km !== null && delivery.distance_km !== undefined && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">
                    Distance Traveled
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    {delivery.distance_km} KM
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Details Card */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-heading text-lg font-bold uppercase">
            Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Customer
                </p>
                <p className="font-semibold text-slate-900">{delivery.customer_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Truck className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Excavator
                </p>
                <p className="font-semibold text-slate-900">{delivery.excavator_model}</p>
                <p className="text-sm text-slate-500">{delivery.excavator_serial}</p>
              </div>
            </div>
            {delivery.assigned_driver_name && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Driver
                  </p>
                  <p className="font-semibold text-slate-900">
                    {delivery.assigned_driver_name}
                  </p>
                  {delivery.driver_vehicle_number && (
                    <p className="text-sm text-amber-600 flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      {delivery.driver_vehicle_number}
                    </p>
                  )}
                </div>
              </div>
            )}
            {delivery.delivery_type && (
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Delivery Type
                  </p>
                  <Badge className={`rounded-none text-xs ${
                    delivery.delivery_type === "Sales" ? "bg-green-100 text-green-800 border-green-300" :
                    delivery.delivery_type === "Rental" ? "bg-blue-100 text-blue-800 border-blue-300" :
                    "bg-slate-100 text-slate-800 border-slate-300"
                  }`}>
                    {delivery.delivery_type.toUpperCase()}
                  </Badge>
                </div>
              </div>
            )}
            {delivery.status === "completed" && (delivery.delivery_order_number || delivery.check_out?.delivery_order_number) && (
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Delivery Order #
                  </p>
                  <p className="font-semibold text-slate-900">
                    {delivery.delivery_order_number || delivery.check_out?.delivery_order_number}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Information */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-heading text-lg font-bold uppercase">
            Delivery Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Hide billing type for drivers */}
            {!isDriver && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Billing Type
                </p>
                <Badge
                  className={`rounded-none text-xs ${
                    delivery.billing_type === "chargeable"
                      ? "bg-green-100 text-green-800 border-green-300"
                      : delivery.billing_type === "partial"
                      ? "bg-amber-100 text-amber-800 border-amber-300"
                      : "bg-slate-100 text-slate-800 border-slate-300"
                  }`}
                >
                  {delivery.billing_type?.replace("-", " ").toUpperCase()}
                </Badge>
              </div>
            )}
            {delivery.scheduled_date && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Scheduled Date
                </p>
                <p className="text-sm text-slate-700">
                  {formatDate(delivery.scheduled_date)}
                </p>
              </div>
            )}
            {delivery.scheduled_time && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-amber-600 font-semibold">
                  Time Slot
                </p>
                <p className="text-sm font-semibold text-amber-700">
                  {delivery.scheduled_time.slice(0, 2)}:{delivery.scheduled_time.slice(2)}
                </p>
              </div>
            )}
            {delivery.pickup_location && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-green-600 font-semibold flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Pickup Location
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-700">{delivery.pickup_location}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.pickup_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-500 text-white rounded-sm hover:bg-green-600"
                    title="Open in Google Maps"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                {(delivery.pickup_contact_person || delivery.pickup_contact_number) && (
                  <p className="text-sm text-green-700 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" />
                    {delivery.pickup_contact_person}
                    {delivery.pickup_contact_person && delivery.pickup_contact_number && " - "}
                    {delivery.pickup_contact_number}
                  </p>
                )}
              </div>
            )}
            {delivery.delivery_location && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-red-600 font-semibold flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Delivery Location
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-700">{delivery.delivery_location}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.delivery_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-500 text-white rounded-sm hover:bg-red-600"
                    title="Open in Google Maps"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                {(delivery.delivery_contact_person || delivery.delivery_contact_number) && (
                  <p className="text-sm text-red-700 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" />
                    {delivery.delivery_contact_person}
                    {delivery.delivery_contact_person && delivery.delivery_contact_number && " - "}
                    {delivery.delivery_contact_number}
                  </p>
                )}
              </div>
            )}
          </div>
          {delivery.notes && (
            <div className="mt-4 p-3 bg-slate-50 rounded-sm">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">
                Notes
              </p>
              <p className="text-sm text-slate-700">{delivery.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery Photos (after checkout) */}
      {(delivery.delivery_photos || delivery.serial_number_photo || delivery.checkout_serial_number_photo || delivery.delivery_order_photo) && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-heading text-lg font-bold uppercase">
              Delivery Photos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Serial Number Photo from Check-in */}
            {delivery.serial_number_photo && (
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-600 font-semibold mb-2">
                  Serial Number Plate (Check-in)
                </p>
                <div
                  className="relative group cursor-pointer inline-block"
                  onClick={() => setEnlargedPhoto(delivery.serial_number_photo)}
                >
                  <img
                    src={delivery.serial_number_photo}
                    alt="Serial Number Plate (Check-in)"
                    className="w-24 h-24 object-cover rounded-sm border border-amber-200 hover:border-amber-400"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-sm flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            )}

            {/* Part Photos */}
            {delivery.delivery_photos && PHOTO_CATEGORIES.map((category) => {
              const photos = delivery.delivery_photos[category.key] || [];
              if (photos.length === 0) return null;
              return (
                <div key={category.key}>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    {category.label} ({photos.length})
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {photos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative group cursor-pointer"
                        onClick={() => setEnlargedPhoto(photo)}
                      >
                        <img
                          src={photo}
                          alt={`${category.label} ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-sm border border-slate-200 hover:border-blue-400"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-sm flex items-center justify-center">
                          <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Serial Number Photo from Check-out */}
            {delivery.checkout_serial_number_photo && (
              <div>
                <p className="text-xs uppercase tracking-wide text-green-600 font-semibold mb-2">
                  Serial Number Plate (Check-out)
                </p>
                <div
                  className="relative group cursor-pointer inline-block"
                  onClick={() => setEnlargedPhoto(delivery.checkout_serial_number_photo)}
                >
                  <img
                    src={delivery.checkout_serial_number_photo}
                    alt="Serial Number Plate (Check-out)"
                    className="w-24 h-24 object-cover rounded-sm border border-green-200 hover:border-green-400"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-sm flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Order Photo */}
            {delivery.delivery_order_photo && (
              <div>
                <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold mb-2">
                  Delivery Order
                </p>
                <div
                  className="relative group cursor-pointer inline-block"
                  onClick={() => setEnlargedPhoto(delivery.delivery_order_photo)}
                >
                  <img
                    src={delivery.delivery_order_photo}
                    alt="Delivery Order"
                    className="w-24 h-24 object-cover rounded-sm border border-blue-200 hover:border-blue-400"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-sm flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Customer Signature (after checkout) */}
      {delivery.customer_signature && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-heading text-lg font-bold uppercase">
              Customer Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <img
              src={delivery.customer_signature}
              alt="Customer signature"
              className="max-w-xs border border-slate-200 rounded-sm bg-white p-2"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DeliveryDetail;
